import { defaultLocale } from "@/locales/config";
import { clearSessionCookies } from "@/utils/cart";
import Cookies from "js-cookie";
import toast from "react-hot-toast";

const shouldLogApiTiming =
  process.env.NODE_ENV === "development" ||
  process.env.NEXT_PUBLIC_DEBUG_API_TIMING === "true";

function logApiCall(info: {
  method: string;
  path: string;
  status: number;
  networkMs: number;
  parseMs?: number;
  totalMs: number;
}) {
  if (!shouldLogApiTiming) return;
  const parse =
    info.parseMs !== undefined ? ` parse=${info.parseMs.toFixed(0)}ms` : "";
}

type NextFetchRequestConfig = {
  revalidate?: number | false;
  tags?: string[];
};

interface FetchAPIOptions {
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  authToken?: string;
  body?: any;
  /** When true, GET uses `no-store` and cache-busting headers (inventory, payments, etc.). Mutations are always no-store. */
  noStore?: boolean;
  /**
   * When true on GET, do not attach `Authorization` from the access_token cookie.
   * Use for public ISR endpoints so cache keys are stable for anonymous users.
   * Explicit `authToken` still sends Authorization.
   */
  publicGet?: boolean;
  next?: NextFetchRequestConfig;
  contentType?: string;
  clearAuthOn406?: boolean;
}

async function getCookieValue(name: string) {
  if (typeof window !== "undefined") {
    return Cookies.get(name);
  }

  try {
    const { cookies } = await import("next/headers");
    const store = await cookies();
    return store.get(name)?.value;
  } catch {
    return undefined;
  }
}

export async function fetchAPI(url: string, options: FetchAPIOptions) {
  const {
    method,
    authToken,
    body,
    noStore,
    next,
    contentType = "application/json",
    publicGet,
    clearAuthOn406 = true,
  } = options;

  const token =
    publicGet && !authToken ? undefined : await getCookieValue("access_token");
  const currency = await getCookieValue("currency");
  // Read the language cookie directly instead of invoking the `getUserLocale`
  // server action — on the client, Server Actions trigger a POST round-trip
  // per call, which was firing on every fetchAPI and showing up as repeated
  // `POST /` entries in server logs.
  const locale = (await getCookieValue("language")) || defaultLocale;

  const isGet = method === "GET";
  const isServer = typeof window === "undefined";
  // Mutations never use the data cache. GETs are ISR/cacheable on the server only; in the
  // browser Next's fetch cache does not apply, so we keep no-store to avoid stale auth'd data.
  const useNoStore = !isGet || noStore === true || (isGet && !isServer);

  const nextConfig: NextFetchRequestConfig = useNoStore
    ? { revalidate: 0, ...next }
    : { revalidate: 60, ...next };

  const headers: RequestInit & { next?: NextFetchRequestConfig } = {
    method,
    headers: {
      "Content-Type": contentType,
      ...(useNoStore
        ? {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        }
        : {}),
      ...(currency ? { "X-Currency": currency.toLowerCase() } : {}),
      ...(locale ? { "X-Language": locale } : {}),
      "X-SECRET-KEY": process.env.NEXT_PUBLIC_API_SECRET_KEY || "",
      ...(authToken || token
        ? { Authorization: `Bearer ${authToken ?? token}` }
        : {}),
    },
    cache: useNoStore ? "no-store" : "default",
    next: nextConfig,
    ...(body && body instanceof FormData
      ? { body }
      : { body: JSON.stringify(body) }),
  };
  const fullPath = url;
  const t0 = performance.now();

  const fetchTimeoutMs = Number(process.env.API_FETCH_TIMEOUT_MS ?? 15000);
  const signal =
    isServer && isGet
      ? AbortSignal.timeout(fetchTimeoutMs)
      : undefined;

  try {
    const tFetch0 = performance.now();
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BASE_URL}${url}`,
      { ...headers, signal },
    );
    const networkMs = performance.now() - tFetch0;

    const contentType = response.headers.get("content-type");
    if (response.status === 406) {
      if (
        clearAuthOn406 &&
        typeof window !== "undefined" &&
        (authToken || token)
      ) {
        clearSessionCookies();
      }
      logApiCall({
        method,
        path: fullPath,
        status: response.status,
        networkMs,
        totalMs: performance.now() - t0,
      });
      return;
    }

    if (
      contentType &&
      contentType.includes("application/json")
      //  && response.ok
    ) {
      const tParse0 = performance.now();
      const result = await response.json();
      const parseMs = performance.now() - tParse0;
      const totalMs = performance.now() - t0;
      logApiCall({
        method,
        path: fullPath,
        status: response.status,
        networkMs,
        parseMs,
        totalMs,
      });
      // toast("Here is your toast.");

      return result;
    } else {
      const totalMs = performance.now() - t0;
      logApiCall({
        method,
        path: fullPath,
        status: response.status,
        networkMs,
        totalMs,
      });
      return { status: response.status, statusText: response.statusText };
    }
  } catch (error) {
    if (shouldLogApiTiming) {
      console.log(
        `[API] ${method} ${fullPath} failed after ${(performance.now() - t0).toFixed(0)}ms`,
        error,
      );
    }
    throw error;
  }
}
